import { Alert, Button, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { browserPage, isChunkLoadError } from '@/app/staleChunk';
import { ApiError, ERROR_CODES } from '@/shared/api/http';
import { useOnline } from '@/shared/hooks/useOnline';

interface LoadErrorProps {
  /** What could not be read, with its article, as the screen names it: "los tickets", "las tareas". */
  what: string;
  /** The error the reading ended with; an `ApiError` lends its reason and its reference code. */
  error: unknown;
  onRetry: () => void;
  /** While the retry is in flight the button shows it, so the notice is not clicked twice. */
  isRetrying?: boolean;
}

/**
 * Guía única, regla 8.3 (`AM-893`): the one notice for a reading that failed. Red, in place of the
 * content: "No pudimos cargar <cosa>.", the reason in one line, the reference code when the API
 * sent one, and "Reintentar". It interrupts the screen reader (rule 8.9): an error is news. It never
 * says the text of an action ("No pudimos completar la acción.") for a reading.
 *
 * A reading that is a piece of the console itself (the manual's index, a page's chunk) fails in
 * another way: the browser keeps the failed load of the module and a second import asks for
 * nothing, so "Reintentar" could never recover it (`staleChunk.ts`). That failure offers "Recargar
 * la página" instead, with the words the root error page already uses (`AM-896`).
 */
export function LoadError({ what, error, onRetry, isRetrying = false }: LoadErrorProps) {
  const { t } = useTranslation('common');
  const online = useOnline();
  const apiError = error instanceof ApiError ? error : null;
  const staleModule = isChunkLoadError(error);
  const reason = staleModule
    ? online
      ? t('error.newVersionDescription')
      : t('error.pageOfflineDescription')
    : apiError?.errorCode === ERROR_CODES.rateLimitExceeded
      ? t('error.rateLimited')
      : apiError?.errorCode === ERROR_CODES.forbidden
        ? t('forbidden.description')
        : t('error.routeDescription');

  return (
    <Alert color="red" variant="light" role="alert" title={t('error.loadTitle', { what })}>
      <Stack gap="xs" align="flex-start">
        <Text size="sm">{reason}</Text>
        {apiError?.correlationId ? (
          <Text size="xs" c="dimmed" className="sl-tabular">
            {t('error.referenceCode', { correlationId: apiError.correlationId })}
          </Text>
        ) : null}
        {staleModule ? (
          <Button size="xs" variant="default" onClick={() => browserPage.reload()}>
            {t('action.reloadPage')}
          </Button>
        ) : (
          <Button size="xs" variant="default" onClick={onRetry} loading={isRetrying}>
            {t('action.retry')}
          </Button>
        )}
      </Stack>
    </Alert>
  );
}
